import React from "react";

/**
 * Is a modal dialog open anywhere on the page?
 *
 * WHY THIS EXISTS: the two floating helper buttons — Ask AI and Feedback — are
 * fixed to the bottom-right of the viewport, and Ask AI sits at a stacking
 * level ABOVE a dialog. So with a dialog open it drew on top of the dialog's
 * own contents and could cover a control the account owner was reaching for.
 * There is nothing useful about a helper button during a modal either: the
 * dialog traps focus and blocks clicks outside itself, so the button is already
 * unreachable — it was only ever in the way.
 *
 * A ONE-LINE Z-INDEX BUMP WOULD NOT HAVE CLOSED THIS. Both buttons, and any
 * floating control added later, need the same answer, and "pick a number below
 * the dialog's" is a rule that has to be re-derived correctly at every new
 * site. Asking one question instead — "is a modal open?" — is the rule.
 *
 * HOW IT KNOWS, and why it is NOT role="dialog". The obvious selector —
 * [role="dialog"][data-state="open"] — is WRONG in this app, and shipping it
 * would have been a visible regression. Radix gives its Popover content the
 * same role="dialog" and the same data-state, and InfoTip (components/common.jsx)
 * is a Popover. Every one of the little "i" icons on nearly every screen would
 * have made both floating buttons vanish and come back.
 *
 * So the signal is the app's OWN marker rather than an inference about a
 * library's roles: the four modal families (dialog, alert-dialog, sheet,
 * drawer) each render an overlay, and only they do. Each overlay carries
 * data-app-modal-overlay. A popover, a tooltip and a dropdown have no overlay
 * and cannot match. Adding a fifth modal family means marking its overlay too
 * — which is a one-line, obvious thing to get right, unlike guessing which
 * ARIA role a primitive happens to use.
 *
 * A MutationObserver on the document body catches both the mount and the
 * data-state flip Radix makes while a modal animates out. One observer, shared
 * by every caller through React's own subscription, so opening a dialog does
 * not cost one observer per floating button.
 */
const listeners = new Set();
let observer = null;
let openNow = false;

const MODAL_OPEN = '[data-app-modal-overlay][data-state="open"]';

const read = () =>
  typeof document !== "undefined" && !!document.querySelector(MODAL_OPEN);

function publish() {
  const next = read();
  if (next === openNow) return;
  openNow = next;
  listeners.forEach((fn) => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  if (!observer && typeof MutationObserver !== "undefined") {
    openNow = read();
    observer = new MutationObserver(publish);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state", "data-app-modal-overlay"] });
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && observer) {
      observer.disconnect();
      observer = null;
    }
  };
}

export function useModalOpen() {
  return React.useSyncExternalStore(subscribe, () => openNow, () => false);
}
